import { Component, computed, ElementRef, Input, OnChanges, OnDestroy, OnInit, Signal, SimpleChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Event, NavigationEnd, Router } from '@angular/router';

import { Observable, of, Subscription } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { DsBreakpoints, DsModalService, DsModalSlideOutAlignment, DsModalVariant, Icons } from '@levelaccess/design-system';

import { SharedModule } from '../../../shared/shared.module';
import { NavService } from '../../../services/navigation/nav.service';
import { WindowService } from '../../../services/window.service';
import { SwitcherModalComponent } from '../../compound-navbar/compound-top-navbar/switcher-modal/switcher-modal.component';
import { INavigationContextItem, NavigationContext } from '../../../services/navigation/nav-structure';
import { SharedCommonUtility } from '../../../../../shared/utils/common.utility';
import { SwitcherFlyoutComponent } from './switcher-flyout/switcher-flyout.component';

const CONTEXT_TYPE_LABELS: Record<NavigationContext, string> = {
  [NavigationContext.tenant]: 'organization',
  [NavigationContext.workspace]: 'workspace',
  [NavigationContext.digitalProperty]: 'digital_asset',
};

@Component({
  selector: 'app-navigation-context-switcher',
  templateUrl: './navigation-context-switcher.component.html',
  styleUrls: ['./navigation-context-switcher.component.scss'],
  standalone: true,
  imports: [SharedModule, SwitcherFlyoutComponent],
})
export class NavigationContextSwitcherComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) public isCollapsed: boolean;

  public currentContextItem: Signal<INavigationContextItem>;
  public contextLabel: Signal<string>;
  public contextIcon: Signal<Icons>;
  public contextType: Signal<string>;
  public isFlyoutOpen: boolean = false;
  public flyoutTop: string = '';
  public flyoutLeft: string = '';

  private isSmallScreen: boolean = false;
  private subscriptions: Subscription = new Subscription();
  private triggerElement: HTMLElement | null = null;

  constructor(
    private navService: NavService,
    private dsModalService: DsModalService,
    private windowService: WindowService,
    private elementRef: ElementRef<HTMLElement>,
    private router: Router,
  ) {
    this.currentContextItem = toSignal(this.getCurrentContextItemObservable());
    this.contextLabel = computed(() => this.currentContextItem()?.label ?? '');
    this.contextIcon = computed(() => this.currentContextItem()?.icon ?? Icons.File);
    this.contextType = computed(() => CONTEXT_TYPE_LABELS[this.currentContextItem()?.type ?? NavigationContext.tenant]);
  }

  private getCurrentContextItemObservable(): Observable<INavigationContextItem> {
    return this.navService.getCurrentContextItem$().pipe(
      switchMap((contextItem: INavigationContextItem) => {
        return SharedCommonUtility.notNullish(contextItem) ? of(contextItem) : this.navService.tenantNavigationContextItem$;
      }),
    );
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.windowService.observeBreakpoint(DsBreakpoints.Small).subscribe((isSmall: boolean) => {
        this.isSmallScreen = isSmall;
        if (isSmall && this.isFlyoutOpen) {
          this.isFlyoutOpen = false;
        }
      }),
    );

    this.subscriptions.add(
      this.router.events.pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
        if (this.isFlyoutOpen) {
          this.closeFlyout();
        }
      }),
    );
  }

  public openSwitcherModal(): void {
    if (this.isSmallScreen) {
      this.dsModalService.open(SwitcherModalComponent, DsModalVariant.slideOut, DsModalSlideOutAlignment.start);
    } else {
      const hostEl: HTMLElement = this.elementRef.nativeElement;
      const buttonRect: DOMRect = hostEl.getBoundingClientRect();

      const navPanel: Element | null = hostEl.closest('app-vertical-navigation-panel');
      const left: number = navPanel ? navPanel.getBoundingClientRect().right : buttonRect.right;

      this.flyoutTop = `${buttonRect.top}px`;
      this.flyoutLeft = `${left}px`;
      this.triggerElement = document.activeElement as HTMLElement;
      this.isFlyoutOpen = true;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['isCollapsed'] && this.isFlyoutOpen) {
      this.closeFlyout();
    }
  }

  public closeFlyout(): void {
    this.isFlyoutOpen = false;
    this.triggerElement?.focus();
    this.triggerElement = null;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
